import React from 'react'
import { useRouter } from 'next/router'
import {
  Accordion,
  Dropdown,
  PageHeader,
  PrimaryLayout,
  SEO,
} from '../../components'
import { buildStaticProps } from '../../ssg/faq'
import { Sanity } from '../../../types/sanity'
import startCase from 'lodash/startCase'

type FAQsReduced = {
  topic: string
  questions: Omit<Sanity.FAQ, 'topic'>[]
}

const reducer: (
  prevVal: FAQsReduced[],
  currVal: Sanity.FAQ,
) => FAQsReduced[] = (acc: FAQsReduced[], val: Sanity.FAQ) => {
  // see if topic already exists on accumulator
  const foundTopicIdx = acc.findIndex((v) => v.topic === val.topic)

  // if topic doesn't exist yet, add it, and add the question
  if (foundTopicIdx === -1) {
    acc.push({
      topic: val.topic,
      questions: [
        {
          _key: val._key,
          question: val.question,
          answer: val.answer,
        },
      ],
    })
    // if topic already exists, add the question
  } else if (foundTopicIdx > -1) {
    acc[foundTopicIdx].questions.push({
      _key: val._key,
      question: val.question,
      answer: val.answer,
    })
  }
  return acc
}

const formatTopic = (topic: string) => startCase(topic.split('-').join(' '))

export default function FAQ(props: Sanity.FAQPage) {
  const [selectedTopic, setSelectedTopic] = React.useState({
    label: '',
    value: '',
  })

  const topics = props.faqs
    .reduce(reducer, [] as FAQsReduced[])
    .sort((a, b) => (a.topic > b.topic ? 1 : -1)) // sort topics alphabetically

  const router = useRouter()

  const handleTopicSelection = (topic: string) => {
    setSelectedTopic({ label: formatTopic(topic), value: topic })
    router.push(`/faq/#${topic}`, undefined, { shallow: true })
  }

  return (
    <>
      <SEO
        title={props.seoTitle || 'Frequently Asked Questions'}
        description={props.seoDescription}
      />
      <PrimaryLayout>
        <div className="px-5 pb-52 sm:px-0">
          <PageHeader title={props.headline} />
          <div className="flex w-full flex-col justify-center lg:flex-row">
            <div
              id="topics-mobile-container"
              className="mx-auto mb-6 block w-full max-w-xs lg:hidden"
            >
              <Dropdown
                id="topics-mobile"
                name="topics"
                onChange={(selectedVal) =>
                  handleTopicSelection(selectedVal.value as string)
                }
                options={topics.reduce((acc, val) => {
                  acc.push({ label: formatTopic(val.topic), value: val.topic })
                  return acc
                }, [] as { label: string; value: string }[])}
                placeholder="Select a Topic"
                value={selectedTopic}
              />
            </div>
            <div
              id="topics-desktop"
              className="c-bg-blur border-white-primary mr-15 top-25 sticky hidden h-fit rounded-xl border-4 px-5 py-8 lg:block"
            >
              <p
                aria-hidden
                className="font-roboto mb-4 px-2 text-xl font-bold"
              >
                TOPICS
              </p>
              <ul role="navigation" aria-label="Topics">
                {topics.map((topic) => (
                  <li
                    className={`${
                      topic.topic === selectedTopic.value
                        ? 'bg-blue-primary text-white-primary'
                        : 'text-blue-navy hover:text-blue-primary bg-transparent'
                    } mb-3 cursor-pointer rounded-full px-2 text-lg font-medium`}
                    key={topic.topic}
                    onClick={() => handleTopicSelection(topic.topic)}
                  >
                    {formatTopic(topic.topic)}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:max-w-xl">
              {topics.map((item, idx) => (
                <div id={item.topic} key={item.topic}>
                  <h2
                    className={`c-topic-heading text-blue-primary ${
                      idx > 0 ? 'my-4 sm:my-6' : 'mb-4 sm:mb-6'
                    } text-2xl font-normal sm:text-3xl`}
                  >
                    {formatTopic(item.topic)}
                  </h2>
                  <ul>
                    {item.questions.map((q) => (
                      <li className="mb-2" key={q._key}>
                        <Accordion question={q.question} answer={q.answer} />
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </PrimaryLayout>
      {/* apply styling to this pseudo element to prevent sticky navbar from covering topic heading: */}
      <style jsx>{`
        .c-topic-heading::before {
          display: block;
          content: ' ';
          margin-top: -100px;
          height: 100px;
          visibility: hidden;
          pointer-events: none;
        }
      `}</style>
    </>
  )
}

export async function getStaticProps(): Promise<{
  props: Sanity.FAQPage
}> {
  return await buildStaticProps()
}
